import { api } from "api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { T_Api_Success_Res } from "./api.types";
import {
  ApplyDriftResult,
  DriftReport,
  DriftSelection,
  OrphanPolicy,
  SyncPlan,
  TaskBoardDto,
  TaskBoardListItemDto,
  UpdateTaskStatusPayload,
} from "dtos/tasks/task-board.dto";
import { Resource } from "dtos/stage.dto";
import { T_MongoId } from "types/_utils.types";

const BOARD_KEY = "task-boards";

const boardListKey = (selector: string, resource?: Resource) => [BOARD_KEY, "list", selector, resource] as const;

const boardDetailKey = (boardId?: T_MongoId | string) => [BOARD_KEY, "detail", boardId] as const;

export const taskBoardService = {
  /**
   * List all task boards for an application.
   * Resolved by app ID or app key via selector.
   */
  useGetByApplication: (selector: string | undefined, resource?: Resource) => {
    return useQuery({
      queryKey: boardListKey(selector ?? "", resource),
      enabled: Boolean(selector),
      queryFn: async () => {
        const res = await api.get(`/task/boards`, { params: { selector, resource } });
        return res.data?.result as TaskBoardListItemDto[];
      },
    });
  },

  /**
   * Manually instantiate a task board from a specific template.
   * On success, invalidates the board list so the tab refreshes and auto-selects.
   */
  useInstantiate: (selector: string | undefined, resource: Resource | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: [BOARD_KEY, "instantiate", selector, resource],
      mutationFn: async (templateId: string) => {
        const res = await api.post(`/task/boards/instantiate`, {
          selector,
          resource,
          templateId,
        });
        return res.data as T_Api_Success_Res<{ created: number; skipped: number }>;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [BOARD_KEY, "list"] });
      },
    });
  },

  /**
   * Fetch a single board in structured grouped form.
   */
  useGetById: (boardId: string | undefined) => {
    return useQuery({
      queryKey: boardDetailKey(boardId),
      enabled: Boolean(boardId),
      queryFn: async () => {
        const res = await api.get(`/task/boards/${boardId}`);
        return res.data?.result as TaskBoardDto;
      },
    });
  },

  /**
   * Update the status of a single task inside a board.
   * On success, invalidates the board detail cache.
   */
  useUpdateTaskStatus: (boardId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: [BOARD_KEY, "update-task-status", boardId],
      mutationFn: async ({ taskId, status }: UpdateTaskStatusPayload) => {
        const res = await api.patch(`/task/boards/${boardId}/tasks/${taskId}/status`, { status });
        return res.data as T_Api_Success_Res<TaskBoardDto>;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: boardDetailKey(boardId) });
      },
    });
  },

  /**
   * Hard delete a task board.
   * On success, invalidates the list cache.
   */
  useDelete: (boardId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: [BOARD_KEY, "delete", boardId],
      mutationFn: async () => {
        const res = await api.delete(`/task/boards/${boardId}`);
        return res.data as T_Api_Success_Res<void>;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [BOARD_KEY, "list"] });
      },
    });
  },

  useAssignTaskMember: (boardId: string | undefined) => {
    return useMutation({
      mutationFn: async (props: any) => {},
    });
  },

  // Add to ApiService.taskBoard in api/api.service.ts
  //
  // import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
  // import { ApplyDriftResult, DriftReport, DriftSelection, OrphanPolicy, SyncPlan } from "dtos/tasks/task-board-sync.dto";

  useGetDrift: (selector: string | undefined, resource: Resource, enabled = true) => {
    return useQuery({
      queryKey: ["task-board-drift", selector, resource],
      enabled: Boolean(selector) && enabled,
      staleTime: 30_000,
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<DriftReport>>(`/task/boards/drift`, {
          params: { selector, resource },
        });
        return data.result;
      },
    });
  },

  useApplyDrift: (selector: string | undefined, resource: Resource) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ selections }: { selections: DriftSelection[] }) => {
        const { data } = await api.post<T_Api_Success_Res<ApplyDriftResult>>(`/task/boards/drift/apply`, {
          selector,
          selections,
        });
        return data.result;
      },
      onSuccess: () => {
        // A board can be created, renamed or restructured here — every board read is stale.
        void queryClient.invalidateQueries({ queryKey: ["task-board-drift", selector, resource] });
        void queryClient.invalidateQueries({ queryKey: ["task-boards", selector] });
        void queryClient.invalidateQueries({ queryKey: ["task-board"] });
      },
    });
  },

  useSyncBoard: (selector: string | undefined, resource: Resource) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ boardId, orphans }: { boardId: string; orphans?: OrphanPolicy }) => {
        const { data } = await api.post<T_Api_Success_Res<SyncPlan>>(`/task/boards/${boardId}/sync`, { orphans });
        return data.result;
      },
      onSuccess: (_plan, { boardId }) => {
        void queryClient.invalidateQueries({ queryKey: ["task-board-drift", selector, resource] });
        void queryClient.invalidateQueries({ queryKey: ["task-board", boardId] });
      },
    });
  },
};
